import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import LandingPage from "@/pages/LandingPage";
import SchedulePage from "@/pages/SchedulePage";
import ContactPage from "@/pages/ContactPage";
import EnrolPage from "@/pages/EnrolPage";
import EnrolThankYouPage from "@/pages/EnrolThankYouPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import PricingPage from "@/pages/PricingPage";
import BookClassPage from "@/pages/BookClassPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import MemberLoginPage from "@/pages/MemberLoginPage";
import MemberSignupPage from "@/pages/MemberSignupPage";
import MemberDashboardPage from "@/pages/MemberDashboardPage";
import ServicePage from "@/pages/ServicePage";
import TextGuidePage from "@/pages/TextGuidePage";
import TextGuidesIndexPage from "@/pages/TextGuidesIndexPage";
import { StickyCallButton } from "@/components/StickyCallButton";
import { SERVICES } from "@/data/services";
import { Toaster } from "@/components/ui/sonner";

// Scroll to top on navigation and push a GTM virtual pageview for the new route
function ScrollToTop() {
  const { pathname } = useLocation();
  const lastTracked = useRef(null);
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    // Guard against a double push for the same URL (React StrictMode re-runs effects in dev)
    if (lastTracked.current === window.location.href) return;
    lastTracked.current = window.location.href;
    // Google Tag Manager owns every destination (GA4, Meta Pixel, Google Ads). GTM's "All Pages"
    // trigger only fires on a hard load, so push a virtual pageview on each client-side route change.
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "virtual_pageview",
        page_path: `${pathname}${window.location.search}`,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [pathname]);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/classes" element={<Navigate to="/pricing" replace />} />
          <Route path="/schedule" element={<SchedulePage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/enrol" element={<EnrolPage />} />
          <Route path="/enrol/thank-you" element={<EnrolThankYouPage />} />
          <Route path="/free-trial" element={<Navigate to="/enrol" replace />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/book/:classId" element={<BookClassPage />} />
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
          <Route path="/member/login" element={<MemberLoginPage />} />
          <Route path="/member/signup" element={<MemberSignupPage />} />
          <Route path="/member/dashboard" element={<MemberDashboardPage />} />
          <Route path="/guides" element={<TextGuidesIndexPage />} />
          <Route path="/guides/:slug" element={<TextGuidePage />} />
          {SERVICES.map((s) => (
            <Route key={s.slug} path={`/${s.slug}`} element={<ServicePage slug={s.slug} />} />
          ))}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <StickyCallButton />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
